import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { ServiceType } from '../../types/enums';
import { resolveImageUrl } from '../../utils/imageUrl';
import styles from './StaffGenderMismatchBlocker.module.css';

/**
 * AB#1842 (PBI-F) — Blocker dialog shown when the owner tries to flip their
 * salon's `ServiceType` from `Everyone` to a restrictive value (`WomenOnly`
 * or `MenOnly`) while staff with the now-mismatched gender still exist.
 *
 * The BE returns:
 *   { code: 'BUSINESS_HAS_INCOMPATIBLE_STAFF',
 *     data: { mismatchedStaffIds: number[] } }
 *
 * The parent passes:
 *   - `targetServiceType` (the value the owner attempted to switch to)
 *   - `mismatchedStaff` (resolved from `mismatchedStaffIds` + the local
 *     staff cache the owner already has loaded in BusinessDetailsSection's
 *     sibling Manage Staff section)
 *
 * Primary CTA: "Resolve first → Manage staff" navigates the owner to
 * `/my-business/staff`. Cancel resets the form's salonType field to its
 * previous value (handled by the parent). The secondary "Request Super
 * Admin override" link transitions to `SuperAdminOverrideRequestModal`.
 *
 * See Dina design handoff §Screen flow B for the wireframe; bilingual copy
 * lives in LanguageContext under `business.serviceType.flip.blocker.*`.
 */

export interface MismatchedStaff {
    id: number;
    fullName: string;
    position?: string | null;
    photoUrl?: string | null;
    gender: 'Male' | 'Female';
}

export interface StaffMismatchBlockingDialogProps {
    isOpen: boolean;
    /** The ServiceType the owner is trying to flip TO. Drives the title,
     *  body copy, and the gender of the staff list pill. */
    targetServiceType: ServiceType;
    mismatchedStaff: readonly MismatchedStaff[];
    onCancel: () => void;
    onResolve: () => void;
    onRequestOverride: () => void;
}

function getInitials(fullName: string): string {
    return (fullName ?? '')
        .trim()
        .split(/\s+/)
        .map(p => p[0] || '')
        .join('')
        .slice(0, 2)
        .toUpperCase() || '?';
}

function resolveStaffPhoto(url: string | null | undefined): string | null {
    if (!url) return null;
    if (/^https?:\/\//i.test(url)) return url;
    return resolveImageUrl(url);
}

/** Substitute `{key}` placeholders in a translated string with the supplied
 *  values. LanguageContext's `t()` is interpolation-free by design, so
 *  callers do this last-mile substitution at render time. */
function interpolate(template: string, vars: Record<string, string | number>): string {
    return template.replace(/\{(\w+)\}/g, (_match, key) => {
        const v = vars[key];
        return v == null ? '' : String(v);
    });
}

const StaffMismatchBlockingDialog: React.FC<StaffMismatchBlockingDialogProps> = ({
    isOpen,
    targetServiceType,
    mismatchedStaff,
    onCancel,
    onResolve,
    onRequestOverride,
}) => {
    const { t } = useLanguage();

    if (!isOpen) return null;

    const isWomenOnly = targetServiceType === ServiceType.WomenOnly;
    const targetLabel = isWomenOnly
        ? t('business.serviceType.womenOnly')
        : t('business.serviceType.menOnly');

    const title = interpolate(
        t(isWomenOnly
            ? 'business.serviceType.flip.blocker.title.womenOnly'
            : 'business.serviceType.flip.blocker.title.menOnly'),
        { n: mismatchedStaff.length },
    );

    const body = interpolate(
        t('business.serviceType.flip.blocker.body'),
        { type: targetLabel },
    );

    return (
        <div className={styles.scrim} role="presentation">
            <div
                className={styles.dialog}
                role="alertdialog"
                aria-labelledby="staff-mismatch-title"
                aria-describedby="staff-mismatch-body"
            >
                <div className={styles.header}>
                    <span className={styles.iconWrap} aria-hidden="true">!</span>
                    <h2 id="staff-mismatch-title" className={styles.title}>{title}</h2>
                </div>

                <div className={styles.body}>
                    <p id="staff-mismatch-body" className={styles.bodyText}>{body}</p>
                    <ul className={styles.staffList}>
                        {mismatchedStaff.map(s => {
                            const photoSrc = resolveStaffPhoto(s.photoUrl);
                            return (
                                <li key={s.id} className={styles.staffRow}>
                                    <span className={styles.staffAvatar} aria-hidden="true">
                                        {photoSrc
                                            ? <img src={photoSrc} alt="" />
                                            : getInitials(s.fullName)}
                                    </span>
                                    <div className={styles.staffInfo}>
                                        <p className={styles.staffName}>{s.fullName}</p>
                                        {s.position && (
                                            <p className={styles.staffMeta}>{s.position}</p>
                                        )}
                                    </div>
                                    <span className={styles.staffPill}>
                                        {s.gender === 'Male'
                                            ? t('staff.fields.gender.option.male')
                                            : t('staff.fields.gender.option.female')}
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                    <p className={styles.bodyInstructions}>
                        {t('business.serviceType.flip.blocker.instructions')}
                    </p>
                </div>

                <div className={styles.footer}>
                    <div className={styles.footerLeft}>
                        <button
                            type="button"
                            className={styles.overrideLink}
                            onClick={onRequestOverride}
                        >
                            {t('business.serviceType.flip.blocker.override.link')}
                        </button>
                    </div>
                    <div className={styles.footerRight}>
                        <button
                            type="button"
                            className={`${styles.btn} ${styles.btnGhost}`}
                            onClick={onCancel}
                        >
                            {t('business.serviceType.flip.blocker.cta.cancel')}
                        </button>
                        <button
                            type="button"
                            className={`${styles.btn} ${styles.btnPrimary}`}
                            onClick={onResolve}
                        >
                            {t('business.serviceType.flip.blocker.cta.resolve')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StaffMismatchBlockingDialog;
